import { getAuth } from 'firebase/auth'
import { nanoid } from 'nanoid'
import * as Sentry from '@sentry/browser'
import { kPaxtonAppApiRESTBaseUrl } from '@/constants/constants-links'
import { formatUnknownError, getResponseDebugContext, getTopLevelKeys, readResponseJson } from '@/util/api/response-body'
import { ApiError } from '@/util/errors/api-error'
import { ApiResponsePayloadError } from '@/util/errors/api-response-payload-error'
import { DeletePreviewRequest, DeletePreviewResponse, DeletePreviewResponseSchema } from '../schemas'
import { kUserFilesDeleteApiBasePath } from '../constants'
import { createUserFilesApiError, formatUserFilesErrorDetail, isExpectedUserFilesApiError } from '@/user_files/utils/api-error'

/**
 * Resolves what deleting a selection would actually remove: the files in use by a Timeline,
 * Medical Chronology, or Medical Billing Summary, and the file counts for each delete option.
 * Selected folders are expanded server-side, so this is the only way the client can warn about
 * files it has never listed. Advisory only — the delete request re-evaluates the selection.
 */
export async function postDeletePreview(request: DeletePreviewRequest): Promise<DeletePreviewResponse> {
  const sentryTransactionId = nanoid()

  try {
    const hasFiles = (request.file_ids?.length ?? 0) > 0
    const hasFolders = (request.folder_ids?.length ?? 0) > 0
    if (!hasFiles && !hasFolders) {
      throw new Error('Delete preview request must include at least one file_id or folder_id')
    }

    const auth = getAuth()
    const token = await auth.currentUser?.getIdToken()

    if (!token) {
      throw new Error('User not authenticated')
    }

    const apiUrl = `${kPaxtonAppApiRESTBaseUrl()}${kUserFilesDeleteApiBasePath}/preview`
    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
        'X-Transaction-Id': sentryTransactionId,
      },
      body: JSON.stringify({
        file_ids: request.file_ids ?? [],
        folder_ids: request.folder_ids ?? [],
      }),
    })

    const { data, parseError } = await readResponseJson(response)
    if (!response.ok) {
      const errorData = parseError ? {} : data
      const error = createUserFilesApiError({ status: response.status, data: errorData, fallbackMessage: `Failed to preview delete: ${response.status}` })
      const detail = formatUserFilesErrorDetail(errorData)

      // A 404 means this endpoint is not deployed yet, or the selection no longer resolves. Both are
      // states the caller handles by failing open to the plain confirmation, so reporting them as
      // exceptions would file one per delete dialog for as long as the backend half is unshipped.
      const isPreviewUnavailable = response.status === 404

      if (!isPreviewUnavailable && !isExpectedUserFilesApiError(error)) {
        Sentry.withScope((scope) => {
          scope.setTags({ transaction_id: sentryTransactionId })
          Sentry.captureException(new Error(`Fetch error - status: ${response.status}, detail: ${detail}`), {
            extra: {
              ...getResponseDebugContext(response, apiUrl),
              requestBody: request,
              responseData: errorData,
              parseError: parseError ? formatUnknownError(parseError) : null,
            },
          })
        })
      }

      throw error
    }

    if (parseError) {
      Sentry.withScope((scope) => {
        scope.setTags({ transaction_id: sentryTransactionId })
        Sentry.captureException(new Error('Delete preview response JSON parse failed'), {
          extra: {
            ...getResponseDebugContext(response, apiUrl),
            requestBody: request,
            parseError: formatUnknownError(parseError),
          },
        })
      })

      throw new ApiResponsePayloadError(response.status, 'Invalid delete preview response JSON')
    }

    const parsed = DeletePreviewResponseSchema.safeParse(data)
    if (!parsed.success) {
      Sentry.withScope((scope) => {
        scope.setTags({ transaction_id: sentryTransactionId })
        Sentry.captureException(new Error('Delete preview response schema validation failed'), {
          extra: {
            ...getResponseDebugContext(response, apiUrl),
            requestBody: request,
            responseKeys: getTopLevelKeys(data),
            issues: parsed.error.issues,
          },
        })
      })

      throw new ApiResponsePayloadError(response.status, 'Invalid delete preview response shape', parsed.error.issues)
    }

    return parsed.data
  } catch (error) {
    if (!(error instanceof ApiError)) {
      Sentry.withScope((scope) => {
        scope.setTags({ transaction_id: sentryTransactionId })
        Sentry.captureException(error, {
          extra: {
            requestBody: request,
          },
        })
      })
    }
    throw error
  }
}
